/**
 * Date Formatting Utilities
 *
 * PARSING
 *   parseDate(input)              → Date object
 *
 * ABSOLUTE FORMATTING (specific date/time display)
 *   formatDateShort(date)         → "Jan 8, 2025"
 *   formatUtcDateShort(date)      → "Jan 8, 2025" (UTC calendar date of an ISO instant)
 *   formatDateTimeShort(date)     → "Jan 8, 2025, 2:30 pm"
 *   formatTime(date)              → "2:30 PM"
 *   formatDateForServer(date)     → "2025-01-08" (for API calls)
 *   toUtcDateString(date)         → "2025-01-08" (UTC calendar date)
 *
 * RELATIVE FORMATTING (contextual/elapsed time)
 *   formatSmartDate(date)         → "today", "tomorrow", "Friday", "Jan 8, 2027"
 *   formatActivityTimestamp(date) → "Today at 2:30 PM", "Last Tuesday"
 *   formatElapsedCompact(date)    → "5m", "3h", "2d"
 *   formatDistanceToNow(date)     → "2 days ago", "in 3 hours"
 *
 * DURATION FORMATTING
 *   formatDuration(minutes)       → "2h 30m"
 */

import {
  addDays,
  formatDistanceToNow as dateFnsFormatDistanceToNow,
  format,
  getDay,
  isSameDay,
  isSameWeek,
  isToday,
  isYesterday,
  startOfWeek,
  subWeeks,
} from "date-fns"

// =============================================================================
// PARSING
// =============================================================================

/**
 * Converts an ISO string date to a JavaScript Date object
 * Safely handles invalid or missing date strings by returning current date
 */
export const parseDate = (dateString: string): Date => {
  if (!dateString) return new Date()

  try {
    return new Date(dateString)
  } catch (error) {
    console.error(`Error converting date ${dateString}:`, error)
    return new Date()
  }
}

/**
 * Type guard for a usable (non-NaN) Date. Use this for values that are already
 * real Date objects (e.g. a date picker's output), since
 * Intl.DateTimeFormat#formatToParts and date-fns#format both throw
 * "RangeError: Invalid time value" on an invalid Date.
 */
export const isValidDate = (date: Date | null | undefined): date is Date =>
  date instanceof Date && !isNaN(date.getTime())

/**
 * Normalizes an API-shaped date field into a real, valid Date — or null when the
 * value is absent or unparseable. Our `Task` types say `deadline_at: Date`, but
 * Rails serializes it as an ISO string, so UI code must coerce before calling
 * Date methods (getHours, etc.), feeding a date picker, or formatting. Prefer
 * this over `isValidDate` for any value sourced from the API.
 */
export const toValidDate = (value: Date | string | null | undefined): Date | null => {
  if (value == null) return null
  const date = value instanceof Date ? value : new Date(value)
  return isValidDate(date) ? date : null
}

// =============================================================================
// ABSOLUTE FORMATTING
// =============================================================================

/**
 * UTC calendar date of a datetime stored as an ISO instant.
 *
 * Date-only fields such as Client Since are persisted as timestamps. List and
 * detail must both read the UTC YYYY-MM-DD prefix so legacy 03:00/04:00 UTC
 * stamps and noon-Eastern stamps render the same day, independent of viewer,
 * book, and household timezone.
 */
export function toUtcDateString(value: Date | string | null | undefined): string | null {
  if (value == null || value === "") return null

  if (typeof value === "string") {
    const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})/)
    if (dateOnly) return dateOnly[1]
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return null
    return parsed.toISOString().slice(0, 10)
  }

  if (isNaN(value.getTime())) return null
  return value.toISOString().slice(0, 10)
}

/**
 * Formats a stored datetime as a calendar date using the UTC date, not a zoned wall clock.
 * @returns "Jan 8, 2025"
 */
export function formatUtcDateShort(value: Date | string | null | undefined): string {
  return formatDateShort(toUtcDateString(value))
}

/**
 * Local midnight Date for the UTC calendar date of a stored instant. Used by date pickers
 * so the selected day matches formatUtcDateShort / toUtcDateString.
 */
export function parseUtcDateLocal(value: Date | string | null | undefined): Date | undefined {
  const isoDate = toUtcDateString(value)
  if (!isoDate) return undefined

  const [year, month, day] = isoDate.split("-").map(Number)
  const date = new Date(year, month - 1, day)
  return isNaN(date.getTime()) ? undefined : date
}

/**
 * YYYY-MM-DD for the calendar day a date picker selected in local time.
 * Do not use toISOString here: local midnight in a positive UTC offset is the previous UTC day.
 */
export function localDateToDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

/**
 * Formats a date for display in tables, forms, and static UI
 * @returns "Jan 8, 2025"
 */
export function formatDateShort(dateString: string | null | undefined | Date): string {
  if (!dateString) return "-"

  if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-").map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  const date = typeof dateString === "string" ? parseDate(dateString) : dateString
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

/**
 * Formats a date with time for timestamps and created_at displays
 * @returns "Jan 8, 2025, 2:30 pm"
 */
export function formatDateTimeShort(dateString: string | null | undefined | Date): string {
  if (!dateString) return "-"

  const date = typeof dateString === "string" ? parseDate(dateString) : dateString

  return date
    .toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/AM|PM/, (match) => match.toLowerCase())
}

/**
 * Formats time only for meeting times and event displays
 * @returns "2:30 PM"
 */
export function formatTime(date: Date | string | null | undefined): string {
  if (!date) return "-"

  const parsedDate = typeof date === "string" ? parseDate(date) : date
  return format(parsedDate, "h:mm a")
}

/**
 * Formats a date string for server submission
 * Ensures dates are in YYYY-MM-DD format for Rails date fields
 * @returns "2025-01-08" or null for invalid dates
 */
export function formatDateForServer(dateString: string | null | undefined): string | null {
  if (!dateString) return null

  try {
    // If already in YYYY-MM-DD format, return as is
    if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString
    }

    // Handle MM/DD/YYYY format
    if (typeof dateString === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      const [month, day, year] = dateString.split("/")
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
    }

    // For other formats, try to parse with Date
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null

    return date.toISOString().split("T")[0]
  } catch {
    return null
  }
}

// =============================================================================
// RELATIVE FORMATTING
// =============================================================================

interface SmartDateOptions {
  preciseTime?: boolean
  timeZone?: string | null
}

/**
 * Returns a Date whose local-time fields match the wall-clock representation
 * of `date` in the given IANA `timeZone`. When `timeZone` is omitted, the
 * input is returned unchanged.
 *
 * Used so that comparisons like `isToday` / `isTomorrow` and absolute
 * formatters like `format(d, "MMM d, yyyy")` — which all read browser-local
 * fields — reflect the target timezone's calendar date.
 */
export function toZonedDate(date: Date | string, timeZone?: string | null): Date {
  const source = date instanceof Date ? date : new Date(date)
  if (!timeZone || isNaN(source.getTime())) return source

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(source)

  const lookup = (type: Intl.DateTimeFormatPartTypes): number =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10)

  const year = lookup("year")
  const month = lookup("month")
  const day = lookup("day")
  // Some browsers report hour=24 for midnight in en-US 24-hour mode.
  const rawHour = lookup("hour")
  const hour = rawHour === 24 ? 0 : rawHour
  const minute = lookup("minute")
  const second = lookup("second")

  return new Date(year, month - 1, day, hour, minute, second)
}

/**
 * True when a deadline's calendar day is before today in the given `timeZone`.
 *
 * All-day deadlines anchor to the book's timezone (see
 * Task#adjust_deadline_for_all_day), so pass the book timezone for them; pass
 * `null`/omit for timed deadlines to compare against the viewer's browser day.
 */
export function isDeadlinePast(date: Date | string, timeZone?: string | null): boolean {
  const zoned = toZonedDate(date, timeZone)
  const todayStart = toZonedDate(new Date(), timeZone)
  todayStart.setHours(0, 0, 0, 0)
  return zoned < todayStart
}

/**
 * Context-aware date formatting for cards, deadlines, and list items
 * Shows relative terms for near dates, falls back to month+day+year
 * @returns "today", "tomorrow", "Friday", "next Monday", "Jan 8, 2027"
 */
export function formatSmartDate(date?: Date | string | null, options: SmartDateOptions = {}): string {
  if (!date) return "-"

  const { preciseTime = false, timeZone } = options
  const parsedDate = toZonedDate(date, timeZone)
  if (!isValidDate(parsedDate)) return "-"
  const now = timeZone ? toZonedDate(new Date(), timeZone) : new Date()

  if (isSameDay(parsedDate, now)) {
    if (!preciseTime) return "today"
    return `today at ${format(parsedDate, "h:mma").toLowerCase()}`
  }

  if (isSameDay(parsedDate, addDays(now, -1))) {
    return "yesterday"
  }

  if (isSameDay(parsedDate, addDays(now, 1))) {
    if (!preciseTime) return "tomorrow"
    return `tomorrow at ${format(parsedDate, "h:mma").toLowerCase()}`
  }
  if (isSameWeek(parsedDate, now, { weekStartsOn: 1 }) && parsedDate > now) {
    return format(parsedDate, "EEEE")
  }

  // Check if date is in the next week
  const nextWeekStart = addDays(now, 7 - getDay(now))
  const nextWeekEnd = addDays(nextWeekStart, 7)

  if (parsedDate >= nextWeekStart && parsedDate <= nextWeekEnd) {
    return `next ${format(parsedDate, "EEEE")}`
  }

  return format(parsedDate, "MMM d, yyyy")
}

/**
 * Verbose timestamp for activity feeds and detailed logs
 * @returns "Today at 2:30 PM", "Yesterday at 3:15 PM", "Last Tuesday", "Two weeks ago"
 */
export function formatActivityTimestamp(date?: Date | string | null): string {
  if (!date) return "-"

  const parsedDate = typeof date === "string" ? new Date(date) : date
  const now = new Date()

  // Today
  // Within the last hour - show relative time
  const diffInMs = now.getTime() - parsedDate.getTime()
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))

  if (diffInMinutes < 60 && diffInMinutes >= 0) {
    return dateFnsFormatDistanceToNow(parsedDate, { addSuffix: true })
  }

  if (isToday(parsedDate)) {
    return `Today at ${format(parsedDate, "h:mm a")}`
  }

  // Yesterday
  if (isYesterday(parsedDate)) {
    return `Yesterday at ${format(parsedDate, "h:mm a")}`
  }

  // Days ago (2-6 days)
  if (isSameWeek(parsedDate, now, { weekStartsOn: 1 })) {
    return `${format(parsedDate, "EEEE")} at ${format(parsedDate, "h:mm a")}`
  }

  // Last week (previous calendar week)
  const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 })
  const lastWeekEnd = startOfWeek(now, { weekStartsOn: 1 })
  if (parsedDate >= lastWeekStart && parsedDate < lastWeekEnd) {
    return `Last ${format(parsedDate, "EEEE")} at ${format(parsedDate, "h:mm a")}`
  }

  // Default fallback
  return `${format(parsedDate, "MMM d, yyyy")} at ${format(parsedDate, "h:mm a")}`
}

/**
 * Ultra-compact elapsed time for notifications, badges, and compact UI
 * @returns "Just now", "5m", "3h", "2d"
 */
export function formatElapsedCompact(date: Date | string | null): string {
  if (!date) return "Just now"

  const parsedDate = typeof date === "string" ? new Date(date) : date
  const now = new Date()
  const diffInMs = now.getTime() - parsedDate.getTime()
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInMinutes < 1) {
    return "Just now"
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m`
  } else if (diffInHours < 24) {
    return `${diffInHours}h`
  } else {
    return `${diffInDays}d`
  }
}

/**
 * Natural language distance from now for comments and updates
 * @returns "2 days ago", "in 3 hours", "less than a minute ago"
 */
export function formatDistanceToNow(date: Date | string | null | undefined): string {
  if (!date) return "-"

  const parsedDate = typeof date === "string" ? new Date(date) : date
  return dateFnsFormatDistanceToNow(parsedDate, { addSuffix: true })
}

// =============================================================================
// DURATION FORMATTING
// =============================================================================

/**
 * Formats a duration in minutes for meeting lengths and time tracking
 * @param minutes Total minutes
 * @returns "2h 30m", "45m", "1h"
 */
export function formatDuration(minutes: number): string {
  if (minutes < 1) return "0m"

  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  if (hours === 0) {
    return `${mins}m`
  } else if (mins === 0) {
    return `${hours}h`
  } else {
    return `${hours}h ${mins}m`
  }
}
